import type { FC, ReactNode } from 'react'
import type { Cart, MenuCategory } from '@/types'

import Link from 'next/link'
import { useEffect, useMemo, useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { formatPrice, fromCents, toCents } from '@/lib/format'
import { buttonClasses } from '@/components/generals/button'

interface OrderBarProps {
    categories: Array<MenuCategory>
    cart: Cart
}

// Floating summary that appears once something is added from the menu, hidden while the order form is on screen.
export const OrderBar: FC<OrderBarProps> = ({ categories, cart }): ReactNode => {
    const [isOrderInView, setIsOrderInView] = useState(false)

    useEffect(() => {
        const target = document.getElementById('order')

        if (!target || typeof IntersectionObserver === 'undefined') return

        const observer = new IntersectionObserver(
            ([entry]) => setIsOrderInView(entry.isIntersecting),
            { rootMargin: '0px 0px -25% 0px' }
        )

        observer.observe(target)

        return () => observer.disconnect()
    }, [])

    const prices = useMemo(() => new Map(categories.flatMap(category => category.items).map(item => [item.id, item.price] as const)), [categories])

    const lines = Object.entries(cart).filter(([id, quantity]) => prices.has(id) && quantity > 0)
    const count = lines.reduce((sum, [, quantity]) => sum + quantity, 0)
    const total = fromCents(lines.reduce((sum, [id, quantity]) => sum + toCents(prices.get(id) ?? 0) * quantity, 0))

    if (count === 0 || isOrderInView) return null

    return (
        <div className='pointer-events-none fixed inset-x-0 bottom-4 z-30 flex justify-center px-4 sm:bottom-6'>
            <div
                role='region'
                aria-label="Your order summary"
                className='pointer-events-auto flex w-full max-w-md items-center justify-between gap-4 rounded-full bg-broth py-2 pl-5 pr-2 text-broth-foreground shadow-2xl shadow-broth/40 animate-in fade-in slide-in-from-bottom-4 duration-300'
            >
                <p className='text-sm' aria-live='polite'>
                    <span className='font-semibold'>
                        {count} {count === 1 ? "item" : "items"}
                    </span>

                    <span className='text-broth-foreground/70'>
                        {' '}for {formatPrice(total)}
                    </span>
                </p>

                <Link href='/#order' className={buttonClasses('primary', 'sm')}>
                    Review order
                    <ArrowRight />
                </Link>
            </div>
        </div>
    )
}
